import { useState } from "react";
import {
  Ban,
  CheckCheck,
  Copy,
  FileDown,
  Hash,
  MoreHorizontal,
  Phone,
  PhoneOff,
  Trash2,
  Undo2,
  Volume2,
} from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import {
  callCounterpart,
  eraseCall,
  eraseCallRecording,
  isCallback,
  type CallDetail,
} from "../../../lib/calls";
import { addInboundBlock } from "../../../lib/callBlocklist";
import { addPhoneSuppression } from "../../../lib/callSuppression";
import type { CrmLabel } from "../../../lib/crmLabels";
import { formatPhone, normalizeE164 } from "../../../lib/phone";
import { useSuppressedNumbers } from "../../telephony/useSuppressedNumbers";
import { Pop, useClosePops } from "../../shell/Pop";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { LabelItems, useCopyToast } from "../../ui";

/**
 * The call's overflow menu.
 *
 * Two erase entries rather than one, because they are two different decisions.
 * "Delete the recording" is what somebody asks for when they object to being
 * recorded, and it keeps the call in the sales history where it belongs.
 * "Delete the call" loses that history and is the rarer request; it leaves a
 * tombstone behind so the erasure stays provable afterwards.
 *
 * Both go through a confirmation and both are irreversible - object storage has
 * no bin for these, and Twilio's copy is gone the moment ours is.
 *
 * This menu acts on the OPEN call, so it can offer the transcript; the list's
 * right-click menu (`CallRowItems`) acts on a row that may never have been
 * fetched and offers what a row carries.
 */
export function CallMenu({
  call,
  labels,
  onPatch,
  onHandled,
  onErased,
  onRecordingErased,
}: {
  call: CallDetail;
  labels: CrmLabel[];
  onPatch: (body: { tags?: string[] }) => Promise<void>;
  /** Tick a waiting call off the callback queue, or put it back. Offered here
   *  as well as on the list row because a voicemail is dealt with while its
   *  transcript is being read, and closing the pane to find the tick is how a
   *  queue stops being worked. */
  onHandled: (handled: boolean) => void;
  /** The whole call is gone - the pane has nothing left to show. */
  onErased?: () => void;
  /** The audio is gone and the call is not. Re-read it rather than guessing. */
  onRecordingErased?: () => void;
}) {
  const t = useT();
  const close = useClosePops();
  const copy = useCopyToast();
  const suppressed = useSuppressedNumbers();
  const [confirming, setConfirming] = useState<"audio" | "call" | "suppress" | "block" | null>(null);
  /**
   * Blocked from HERE, this session.
   *
   * Nothing in the shell holds this list - unlike the do-not-call numbers,
   * which every operator loads at sign-in to strike through - so there is no
   * shared set to ask. Without this the item would still be sitting in the menu
   * after it had been used, and the second press would look like the first one
   * had not worked.
   */
  const [justBlocked, setJustBlocked] = useState(false);

  const hasAudio = call.recordingStatus === "stored";
  const counterpart = callCounterpart(call);
  const marked = !!suppressed?.has(normalizeE164(counterpart));

  async function runConfirmed() {
    if (confirming === "audio") {
      await eraseCallRecording(call.id).catch(() => {});
      onRecordingErased?.();
    } else if (confirming === "call") {
      await eraseCall(call.id).catch(() => {});
      onErased?.();
    } else if (confirming === "suppress") {
      // `source: "unsubscribe"` is the point, not a detail: it is what makes
      // the entry one nobody here can undo later.
      const res = await addPhoneSuppression({ phone: counterpart, source: "unsubscribe" }).catch(() => null);
      if (res) suppressed?.add(res.suppression.value);
    } else if (confirming === "block") {
      // `source: "spam"` rather than `manual`, and the difference is what the
      // entry can be trusted to mean in six months: a person heard this caller
      // and judged it. The blocklist screen's own form can only write `manual`.
      const res = await addInboundBlock({ phone: counterpart, source: "spam" }).catch(() => null);
      if (res) setJustBlocked(true);
    }
    setConfirming(null);
  }

  const copyAndClose = (value: string) => {
    close();
    copy(value);
  };

  function downloadTranscript() {
    close();
    if (!call.transcriptText) return;
    const stamp = call.startedAt.slice(0, 10);
    const who = (call.companyName ?? callCounterpart(call))
      .replace(/[^\p{L}\p{N}]+/gu, "-")
      .replace(/^-|-$/g, "");
    // A blob URL rather than a data: one - a long transcript overflows what a
    // data URI can carry in some browsers, and this is the same two lines.
    const url = URL.createObjectURL(new Blob([call.transcriptText], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${stamp}-${who || "call"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
    <Pop
      trigger={(open) => (
        <button
          type="button"
          className={`crm-cb${open ? " is-on" : ""}`}
          title={t("common.more")}
          aria-label={t("common.more")}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <MoreHorizontal className="size-4" />
        </button>
      )}
    >
      {/* The other party's number. `toNumber` is our own Twilio line on an
          inbound row, and copying that is never what "copy number" means. */}
      {callCounterpart(call) ? (
        <button type="button" className="crm-mi" onClick={() => copyAndClose(callCounterpart(call))}>
          <Phone className="size-4" />
          {t("calls.detail.copyNumber")}
        </button>
      ) : null}
      {call.twilioCallSid ? (
        <button type="button" className="crm-mi" onClick={() => copyAndClose(call.twilioCallSid!)}>
          <Hash className="size-4" />
          {t("calls.detail.copySid")}
        </button>
      ) : null}
      {call.transcriptText ? (
        <button type="button" className="crm-mi" onClick={downloadTranscript}>
          <FileDown className="size-4" />
          {t("calls.detail.downloadTranscript")}
        </button>
      ) : null}
      <button type="button" className="crm-mi" onClick={() => copyAndClose(call.id)}>
        <Copy className="size-4" />
        {t("calls.detail.copyId")}
      </button>

      {/* Only on the rows the callback queue holds. The server refuses anything
          else with a 409, and an entry that reports success and changes nothing
          teaches an operator to stop trusting the menu. */}
      {isCallback(call) ? (
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            onHandled(!call.handledAt);
          }}
        >
          {call.handledAt ? <Undo2 className="size-4" /> : <CheckCheck className="size-4" />}
          {t(call.handledAt ? "calls.callback.undo" : "calls.callback.done")}
        </button>
      ) : null}

      {/* The menu stays open through a label click: two labels on one call is
          the normal case, and closing after each would mean reopening. */}
      <LabelItems
        title={t("calls.pane.labels")}
        labels={labels}
        tags={call.tags ?? []}
        onApply={(next) => onPatch({ tags: next })}
      />

      {/* Above the danger entries and separated from them, because this is not
          a destructive act - it is writing down what the person said. The
          number is the counterparty's, never our own line. */}
      {counterpart && !marked ? (
        <>
          <div className="crm-menu-sep" />
          <button
            type="button"
            className="crm-mi"
            onClick={() => {
              close();
              setConfirming("suppress");
            }}
          >
            <PhoneOff className="size-4" />
            {t("calls.suppression.mark")}
          </button>
        </>
      ) : null}

      {/* INBOUND ONLY, and that is the rule rather than caution. The list is
          read by one guard, on the webhook that answers calls coming IN, so
          blocking the number of somebody we rang would write a row that changes
          nothing anybody can see - a menu item that quietly does nothing is
          worse than no menu item.

          Beside the do-not-call entry because they are the pair and they are
          opposites: that one writes down what the caller asked of us, this one
          is our own decision about them. Not red - a refused robot is
          housekeeping, and the destructive entries below have to keep their
          colour to themselves. */}
      {counterpart && call.direction === "inbound" && !justBlocked ? (
        <>
          <div className="crm-menu-sep" />
          <button
            type="button"
            className="crm-mi"
            onClick={() => {
              close();
              setConfirming("block");
            }}
          >
            <Ban className="size-4" />
            {t("calls.blocklist.mark")}
          </button>
        </>
      ) : null}

      {/* Last in the menu and only when there is audio to remove: an entry that
          offers to delete a recording this call never had is an entry that
          teaches the operator to distrust the menu. */}
      {hasAudio ? (
        <button
          type="button"
          className="crm-mi crm-mi-danger"
          onClick={() => {
            close();
            setConfirming("audio");
          }}
        >
          <Volume2 className="size-4" />
          {t("calls.detail.eraseRecording")}
        </button>
      ) : null}
      <button
        type="button"
        className="crm-mi crm-mi-danger"
        onClick={() => {
          close();
          setConfirming("call");
        }}
      >
        <Trash2 className="size-4" />
        {t("calls.detail.eraseCall")}
      </button>
    </Pop>

    {/* Outside the Pop: the menu closes as the dialog opens, and a modal
        rendered inside a popover that is unmounting goes with it. */}
    {confirming ? (
      <ConfirmModal
        title={t(
          confirming === "suppress"
            ? "calls.suppression.markTitle"
            : confirming === "block"
              ? "calls.blocklist.markTitle"
              : confirming === "audio"
                ? "calls.detail.eraseRecordingTitle"
                : "calls.detail.eraseCallTitle",
        )}
        body={
          confirming === "suppress"
            ? t("calls.suppression.markBody", { phone: formatPhone(normalizeE164(counterpart) ?? counterpart) })
            : confirming === "block"
              ? t("calls.blocklist.markBody", { phone: formatPhone(normalizeE164(counterpart) ?? counterpart) })
              : t(confirming === "audio" ? "calls.detail.eraseRecordingBody" : "calls.detail.eraseCallBody")
        }
        confirmLabel={t(
          confirming === "suppress"
            ? "calls.suppression.mark"
            : confirming === "block"
              ? "calls.blocklist.mark"
              : confirming === "audio"
                ? "calls.detail.eraseRecording"
                : "calls.detail.eraseCall",
        )}
        cancelLabel={t("common.cancel")}
        onConfirm={runConfirmed}
        onClose={() => setConfirming(null)}
      />
    ) : null}
    </>
  );
}
